import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  concatMap,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  toArray,
} from 'rxjs';
import {
  WorkspaceAssetModel,
  WorkspaceAssetService,
} from '@core/services/workspace-asset.service';
import {
  AccountPlanLimitService,
  isMaxWorkspaceAssetsLimitError,
} from '@core/services/account-plan-limit.service';
import { UiTranslationService } from '@core/services/ui-translation.service';

type ImportQueueStatus = 'pending' | 'uploading' | 'success' | 'error';

type ImportQueueItem = {
  id: number;
  file: File;
  status: ImportQueueStatus;
  errorMessage: string | null;
  importResult: WorkspaceAssetModel | null;
};

type ImportSummary = {
  total: number;
  importedCount: number;
  failedCount: number;
};

@Component({
  selector: 'app-workspace-asset-modal',
  templateUrl: './workspace-asset-modal.html',
  styleUrl: './workspace-asset-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceAssetModal {
  private static readonly maxFileSizeBytes = 5 * 1024 * 1024;
  private static readonly maxFileNameLength = 260;
  private static readonly maxFileExtensionLength = 10;
  private static readonly maxMimeTypeLength = 80;
  private static readonly allowedExtensions = ['.txt', '.doc', '.docx', '.pdf'];
  private static readonly allowedMimeTypesByExtension: Readonly<
    Record<string, readonly string[]>
  > = {
    '.txt': ['text/plain'],
    '.doc': ['application/msword'],
    '.docx': [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    '.pdf': ['application/pdf'],
  };

  readonly activeModal = inject(NgbActiveModal);
  private readonly workspaceAssetService = inject(WorkspaceAssetService);
  private readonly accountPlanLimitService = inject(AccountPlanLimitService);
  private readonly translations =
    inject(UiTranslationService).namespace('modals');
  readonly t = this.translations.t;

  private nextItemId = 1;

  readonly fileItems = signal<ImportQueueItem[]>([]);
  readonly isDragOver = signal(false);
  readonly isUploading = signal(false);
  readonly summary = signal<ImportSummary | null>(null);
  readonly infoMessage = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);

  readonly pendingCount = computed(
    () => this.fileItems().filter((item) => item.status === 'pending').length,
  );
  readonly canImport = computed(
    () => this.pendingCount() > 0 && !this.isUploading(),
  );

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.isUploading()) {
      return;
    }

    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.isUploading()) {
      return;
    }

    this.isDragOver.set(false);
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  onFileSelectionChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  addFiles(fileList: FileList | null): void {
    if (!fileList?.length) {
      return;
    }

    this.summary.set(null);
    this.infoMessage.set(null);
    this.errorMessage.set(null);

    const files = Array.from(fileList);
    const nextItems = files.map((file) => {
      const validationMessage = this.validateFile(file);
      return {
        id: this.nextItemId++,
        file,
        status: validationMessage ? 'error' : 'pending',
        errorMessage: validationMessage,
        importResult: null,
      } satisfies ImportQueueItem;
    });

    this.fileItems.update((current) => [...current, ...nextItems]);
  }

  removeItem(itemId: number): void {
    if (this.isUploading()) {
      return;
    }

    this.fileItems.update((current) =>
      current.filter((item) => item.id !== itemId),
    );
  }

  workspaceAssets(): void {
    if (this.isUploading()) {
      return;
    }

    const queuedIds = this.fileItems()
      .filter((item) => item.status === 'pending')
      .map((item) => item.id);

    if (!queuedIds.length) {
      return;
    }

    this.isUploading.set(true);
    this.summary.set(null);
    this.infoMessage.set(null);
    this.errorMessage.set(null);

    this.accountPlanLimitService
      .canCreateWorkspaceAssetsFresh(queuedIds.length)
      .pipe(
        switchMap((canImport) => {
          if (!canImport) {
            this.errorMessage.set(
              this.translations.tr(
                'importSource.maxWorkspaceAssetsLimitReached',
              ),
            );
            return of(null);
          }

          return from(queuedIds).pipe(
            concatMap((itemId) => this.uploadItem(itemId)),
            toArray(),
          );
        }),
        finalize(() => this.isUploading.set(false)),
      )
      .subscribe((uploadResults) => {
        if (uploadResults === null) {
          return;
        }

        const finalItems = this.fileItems();
        const importedCount = queuedIds.filter(
          (itemId) => this.getItemStatus(finalItems, itemId) === 'success',
        ).length;
        const failedCount = queuedIds.filter(
          (itemId) => this.getItemStatus(finalItems, itemId) === 'error',
        ).length;

        const summary = {
          total: queuedIds.length,
          importedCount,
          failedCount,
        } satisfies ImportSummary;

        this.summary.set(summary);

        if (failedCount === 0 && importedCount > 0) {
          this.activeModal.close({
            importedCount,
            failedCount,
          });
          return;
        }

        this.errorMessage.set(
          this.translations.tr('importSource.errorSomeFailed'),
        );
      });
  }

  formatFileSize(fileSize: number): string {
    if (fileSize < 1024) {
      return `${fileSize} B`;
    }

    if (fileSize < 1024 * 1024) {
      return `${(fileSize / 1024).toFixed(1)} KB`;
    }

    return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  getStatusBadgeClass(status: ImportQueueStatus): string {
    if (status === 'success') {
      return 'badge text-bg-success';
    }

    if (status === 'error') {
      return 'badge text-bg-danger';
    }

    if (status === 'uploading') {
      return 'badge text-bg-warning';
    }

    return 'badge text-bg-light';
  }

  getStatusLabel(status: ImportQueueStatus): string {
    if (status === 'success') {
      return this.translations.tr('importSource.statusImported');
    }

    if (status === 'error') {
      return this.translations.tr('importSource.statusFailed');
    }

    if (status === 'uploading') {
      return this.translations.tr('importSource.statusUploading');
    }

    return this.translations.tr('importSource.statusPending');
  }

  private uploadItem(itemId: number): Observable<void> {
    const item = this.fileItems().find((entry) => entry.id === itemId);
    if (!item) {
      return of(void 0);
    }

    this.updateItem(itemId, {
      status: 'uploading',
      errorMessage: null,
      importResult: null,
    });

    return this.workspaceAssetService
      .upload({
        file: item.file,
        mimeType: item.file.type || undefined,
      })
      .pipe(
        switchMap((result) => {
          this.updateItem(itemId, {
            status: 'success',
            errorMessage: null,
            importResult: result,
          });

          return this.accountPlanLimitService.refresh().pipe(map(() => void 0));
        }),
        catchError((error: unknown) => {
          this.updateItem(itemId, {
            status: 'error',
            errorMessage: isMaxWorkspaceAssetsLimitError(error)
              ? this.translations.tr(
                  'importSource.maxWorkspaceAssetsLimitReached',
                )
              : ((error as Error)?.message ??
                this.translations.tr('importSource.errorUploadFile')),
            importResult: null,
          });
          return of(void 0);
        }),
      );
  }

  private updateItem(itemId: number, patch: Partial<ImportQueueItem>): void {
    this.fileItems.update((current) =>
      current.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        return {
          ...item,
          ...patch,
        };
      }),
    );
  }

  private validateFile(file: File): string | null {
    const extension = this.extractExtension(file.name);
    if (file.name.length > WorkspaceAssetModal.maxFileNameLength) {
      return this.translations.tr('importSource.errorFileNameLength');
    }

    if (extension.length > WorkspaceAssetModal.maxFileExtensionLength) {
      return this.translations.tr('importSource.errorFileExtensionLength');
    }

    if (file.type.length > WorkspaceAssetModal.maxMimeTypeLength) {
      return this.translations.tr('importSource.errorMimeTypeLength');
    }

    if (!WorkspaceAssetModal.allowedExtensions.includes(extension)) {
      return this.translations.tr('importSource.errorUnsupportedType');
    }

    const normalizedMimeType = file.type.trim().toLowerCase();
    const allowedMimeTypes =
      WorkspaceAssetModal.allowedMimeTypesByExtension[extension];
    if (normalizedMimeType && !allowedMimeTypes.includes(normalizedMimeType)) {
      return this.translations.tr('importSource.errorUnsupportedType');
    }

    if (file.size > WorkspaceAssetModal.maxFileSizeBytes) {
      return this.translations.tr('importSource.errorFileSize');
    }

    return null;
  }

  private extractExtension(fileName: string): string {
    const index = fileName.lastIndexOf('.');
    if (index < 0) {
      return '';
    }

    return fileName.slice(index).toLowerCase();
  }

  private getItemStatus(
    items: ImportQueueItem[],
    itemId: number,
  ): ImportQueueStatus | null {
    const item = items.find((entry) => entry.id === itemId);
    return item?.status ?? null;
  }
}
